import { useFormContext, Controller } from 'react-hook-form';
import { Button, Box, Avatar } from '@mui/material';
import { useState, useEffect } from 'react';
import { getProfilePictureApi, uploadProfilePictureApi } from 'src/api';
// Import image as a module (webpack will transform this to a URL)
import DEFAULT_UNKNOWN_PROFILE_PICTURE from 'src/assets/img/DefaultUnknownProfilePicture.png';

interface RHFFileUploadProps {
  name: string;
  label?: string;
}

// TODO: Make profile picture reload on upload
export default function RHFFileUpload({ name, label }: RHFFileUploadProps) {
  const { control } = useFormContext();
  const [preview, setPreview] = useState<string | undefined>();
  // Allow string (for URLs) or any (for imported static assets)
  const [currentProfilePic, setCurrentProfilePic] = useState<string | any>();

  // Fetch existing profile picture on component mount
  useEffect(() => {
    const loadProfilePicture = async () => {
      try {
        // Import persistStore to use proper storage methods
        const { persistStore } = await import('src/persist-store');
        
        // First try to get from sessionStorage for quick display (better than localStorage for user sessions)
        const profilePicUrl = await persistStore.getSession<string>('profilePictureURL');
        if (profilePicUrl) {
          setCurrentProfilePic(profilePicUrl);
          console.debug('Profile picture loaded from session storage');
        }
        
        // Then fetch from API to ensure we have the latest
        const response = await getProfilePictureApi();
        if (response?.profilePictureURL) {
          setCurrentProfilePic(response.profilePictureURL);
          // Update sessionStorage for future quick loads within this session
          await persistStore.putSession('profilePictureURL', response.profilePictureURL);
          console.debug('Profile picture updated from API');
        } else if (!profilePicUrl) {
          // If no profile pic in storage or API, use default
          console.debug('Using default profile picture');
          setCurrentProfilePic(DEFAULT_UNKNOWN_PROFILE_PICTURE);
        }
      } catch (error) {
        console.error('Error loading profile picture:', error);
        // Use default if API call fails
        if (!currentProfilePic) {
          setCurrentProfilePic(DEFAULT_UNKNOWN_PROFILE_PICTURE);
        }
      }
    };
    
    loadProfilePicture();
  }, [currentProfilePic]);

  const handleFilePreview = async (file: File) => {
    const fileUrl = URL.createObjectURL(file);
    setPreview(fileUrl);
    
    try {
      // Import persistStore to use proper storage methods
      const { persistStore } = await import('src/persist-store');
      
      // When a new file is selected, clear any previous profile picture URL from session storage
      await persistStore.removeSession('profilePictureURL');
      console.debug('Cleared previous profile picture from session storage');
    } catch (error) {
      console.error('Error clearing previous profile picture:', error);
    }
    
    // Cleanup the URL when preview changes
    return () => URL.revokeObjectURL(fileUrl);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'start', gap: 2 }}>
          <Avatar
            src={preview || currentProfilePic}
            sx={{ 
              width: 100, 
              height: 100,
              border: '1px solid #ccc'
            }}
          />
          <Button
            variant="outlined"
            component="label"
            sx={{ mt: 1 }}
            style={{
              border: '1px solid #fff',
              color: '#fff',
              backgroundColor: 'transparent',
              borderRadius: '9999px',
              textTransform: 'none',
            }}
          >
            {label || 'Choose Profile Picture'}
            <input
              type="file"
              hidden
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  onChange(file); // Update form value
                  handleFilePreview(file);
                  uploadProfilePictureApi(file);
                }
              }}
            />
          </Button>
          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.75rem' }}>
              {error.message}
            </Box>
          )}
        </Box>
      )}
    />
  );
}
